import React, { useState, useEffect, useRef, useCallback } from 'react';
// Import all API functions from the main api_services file
import { 
    getOngoingTradeStatus,
    getDirectionHistory,
    clearDirectionHistory
} from "../api_services/api_services";

import {
    start_capture as startCapture,
    stop_capture as stopCapture,
    get_capture_status as getCaptureStatus,
    getDirections,
    getStreakCount,
    getLatestStreakCount,
    setAmount,
    getLatestAmount, stop_capture, start_capture
} from "../api_services/start_capture";
import './Dashboard.css';

const POLLING_INTERVAL = 5000;

export const Dashboard = () => {
    const [theme, setTheme] = useState('light'); // 'light' or 'dark'
    const [isCapturing, setIsCapturing] = useState(false);
    const [status, setStatus] = useState('idle');
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isRestarting, setIsRestarting] = useState(false);
    const [stats, setStats] = useState({
        captureCount: 0,
        lastCaptured: null,
        directions: [],
        numberOfDirections: null,
        streakCount: 0,
        latestStreakCount: 0,
        amount: 0,
        latestAmount: 0,
        currentStreak: 0
    });
    
    const [tradeStatus, setTradeStatus] = useState({
        active: false,
        direction: null,
        message: 'No active trade'
    });
    
    const [amountInput, setAmountInput] = useState('');
    const [directionHistory, setDirectionHistory] = useState([]);
    const [historyPage, setHistoryPage] = useState(1);
    const [historyLimit] = useState(10);
    const [totalHistory, setTotalHistory] = useState(0);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    
    const pollingRef = useRef(null);

    // Fetch direction history
    const fetchDirectionHistory = async (page = 1, limit = 10) => {
        try {
            setIsLoadingHistory(true);
            const result = await getDirectionHistory(page, limit);
            if (result && result.directionHistory) {
                setDirectionHistory(result.directionHistory);
                setTotalHistory(parseInt(result.total) || 0);
                setHistoryPage(page);
            }
        } catch (err) {
            console.error('Failed to fetch direction history:', err);
            setError(prev => ({
                ...prev,
                message: 'Failed to load direction history',
                details: err.message
            }));
        } finally {
            setIsLoadingHistory(false);
        }
    };

    // Clear direction history
    const handleClearHistory = async () => {
        if (window.confirm('Are you sure you want to clear all direction history? This action cannot be undone.')) {
            try {
                await clearDirectionHistory();
                await fetchDirectionHistory(1, historyLimit);
            } catch (err) {
                console.error('Failed to clear direction history:', err);
                setError(prev => ({
                    ...prev,
                    message: 'Failed to clear direction history',
                    details: err.message
                }));
            }
        }
    };

    // Fetch and update capture status
    const fetchStatus = async () => {
        try {
            const [statusData, tradeData, latestStreak] = await Promise.all([
                getCaptureStatus(),
                getOngoingTradeStatus().catch(err => ({
                    success: false,
                    direction: null,
                    message: 'no active trade'
                })),
                getLatestStreakCount().catch(() => ({ count: 0 }))
            ]);

            const isActive = statusData.isCapturing || false;
            const statusMessage = statusData.message || (isActive ? 'Bot is running' : 'Bot is idle');
            
            setIsCapturing(isActive);
            setStatus(isActive ? 'active' : 'idle');
            
            // Update trade status
            setTradeStatus({
                active: tradeData.success || false,
                direction: tradeData.direction || null,
                message: tradeData.message || 'No active trade',
                time_placement: tradeData.time_placement || null
            });
            
            // Update stats with the latest data
            const update = {
                lastCaptured: isActive ? new Date().toISOString() : stats.lastCaptured,
                statusMessage,
                currentStreak: latestStreak.count || 0
            };

            setStats(prev => ({
                ...prev,
                ...update,
                ...(isActive ? { lastCaptured: new Date().toISOString() } : {})
            }));
            
            return statusData;
        } catch (err) {
            console.error('Failed to fetch status:', err);
            setError('Failed to fetch capture status. Please try refreshing the page.');
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const startPolling = () => {
        // Clear any existing interval
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
        }

        fetchStatus();
        
        // Set up polling
        pollingRef.current = setInterval(() => {
            fetchStatus();
        }, POLLING_INTERVAL);
    };

    // Stop polling
    const stopPolling = () => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
    };

    const handleStartCapture = async () => {
        try {
            setStatus('starting');
            setError(null);
            const result = await startCapture();
            
            if (result.success) {
                // Refresh status after starting capture
                await fetchStatus();
                startPolling();
            } else {
                throw new Error(result.message || 'Failed to start capture');
            }
        } catch (err) {
            setError(err.message || 'An error occurred while starting capture');
            setStatus('error');
        }
    };

    const handleStopCapture = async () => {
        try {
            setStatus('stopping');
            setError(null);
            const result = await stopCapture();
            
            if (result.success) {
                // Refresh status after stopping capture
                await fetchStatus();
            } else {
                throw new Error(result.message || 'Failed to stop capture');
            }
        } catch (err) {
            setError(err.message || 'An error occurred while stopping capture');
            setStatus('error');
        }
    };

    // Toggle between light and dark theme
    const toggleTheme = useCallback(() => {
        const newTheme = theme === 'light' ? 'dark' : 'light';
        setTheme(newTheme);
        document.documentElement.setAttribute('data-theme', newTheme);
        localStorage.setItem('theme', newTheme);
    }, [theme]);

    // Initialize component
    useEffect(() => {
        const initialize = async () => {
            await fetchStatus();
            await fetchLatestStreakCount();
            startPolling();
        };
        
        initialize();
        // Load initial direction history
        fetchDirectionHistory(1, historyLimit);
        return () => {
            stopPolling();
        };
    }, []);

    useEffect(() => {
        const savedTheme = localStorage.getItem('theme');
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        const initialTheme = savedTheme || (prefersDark ? 'dark' : 'light');
        
        setTheme(initialTheme);
        document.documentElement.setAttribute('data-theme', initialTheme);
    }, []);

    // Add theme class to body when theme changes
    useEffect(() => {
        document.body.className = `${theme}-theme`;
    }, [theme]);

    const fetchLatestStreakCount = async () => {
        try {
            const result = await getLatestStreakCount();
            console.log('Latest streak count response:', result); // Debug log
            
            // Update both streak count and number of directions from the API response
            setStats(prev => ({
                ...prev,
                latestStreakCount: result.number_of_directions || 0,
                numberOfDirections: result.number_of_directions || prev.numberOfDirections,
                streakCount: result.number_of_directions || 0
            }));
        } catch (err) {
            console.error('Failed to fetch latest streak count:', err);
        }
    };

    // Function to fetch latest amount
    const fetchLatestAmount = async () => {
        try {
            const result = await getLatestAmount();
            const amount = parseFloat(result.amount) || 0;
            setStats(prev => ({
                ...prev,
                amount: amount,
                latestAmount: amount
            }));
        } catch (err) {
            console.error('Failed to fetch latest amount:', err);
            setStats(prev => ({
                ...prev,
                amount: 0,
                latestAmount: 0
            }));
        }
    };

    // Handle amount submission
    const handleAmountSubmit = async (e) => {
        e.preventDefault();
        const amount = parseFloat(amountInput);
        if (isNaN(amount) || amount <= 0) return;
        
        try {
            await setAmount(amount);
            setAmountInput('');
            await fetchLatestAmount();
        } catch (err) {
            console.error('Failed to set amount:', err);
        }
    };

    // Fetch data on component mount
    useEffect(() => {
        const fetchInitialData = async () => {
            await fetchLatestStreakCount();
            await fetchLatestAmount();
        };
        
        fetchInitialData();
    }, []);

    // Fetch streak count when numberOfDirections changes
    useEffect(() => {
        const fetchStreakCount = async () => {
            try {
                const result = await getStreakCount(stats.numberOfDirections);
                setStats(prev => ({
                    ...prev,
                    streakCount: result.streakCount || 0
                }));
            } catch (err) {
                console.error('Failed to fetch streak count:', err);
            }
        };
        
        fetchStreakCount();
    }, [stats.numberOfDirections]);

    // Fetch available directions on mount and update when isCapturing changes
    useEffect(() => {
        const fetchDirections = async () => {
            try {
                const result = await getDirections();

                if (Array.isArray(result)) {
                    setStats(prev => ({
                        ...prev,
                        directions: result
                    }));
                }
                else if (result && result.data && Array.isArray(result.data)) {
                    setStats(prev => ({
                        ...prev,
                        directions: result.data
                    }));
                }
                // Handle case where directions are in a directions property
                else if (result && result.directions && Array.isArray(result.directions)) {
                    setStats(prev => ({
                        ...prev,
                        directions: result.directions
                    }));
                }
            } catch (err) {
                console.error('Failed to fetch directions:', err);
                // Set empty array to prevent render errors
                setStats(prev => ({
                    ...prev,
                    directions: []
                }));
            }
        };
        
        fetchDirections();
        
        // Set up polling for directions when capturing
        let intervalId;
        if (isCapturing) {
            intervalId = setInterval(fetchDirections, 5000);
        }
        
        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [isCapturing]);

    // Render loading state
    if (isLoading) {
        return (
            <div className="dashboard loading">
                <div className="loading-spinner"></div>
                <p>Loading dashboard...</p>
            </div>
        );
    }

    const restart_capture = async () => {
        try {
            setIsRestarting(true);
            setStatus('restarting');
            
            // Stop the capture first
            await stop_capture();
            
            // Small delay before starting again
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Start the capture again
            await start_capture();
            
            // Update the status
            setStatus('active');
            await fetchStatus();
            
        } catch (err) {
            console.error('Failed to restart capture:', err);
            setError({
                message: 'Failed to restart bot',
                details: err.message || 'An unknown error occurred while restarting the bot.'
            });
            setStatus('error');
        } finally {
            setIsRestarting(false);
        }
    };

    return (
        <div className="dashboard">
            <div className="dashboard-header">
                <div className="header-content">
                    <h1>ZIZ DIGITAL</h1>
                    <div className="header-actions">
                        <button
                            onClick={toggleTheme}
                            className="theme-toggle"
                            aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
                            title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
                        >
                            {theme === 'light' ? '🌙' : '☀️'}
                        </button>

                        {isCapturing && (
                            <div className="header-status">
                                <span className="status-badge">
                                    <span className="pulse"></span>
                                    LIVE
                                </span>
                                <button
                                    onClick={restart_capture}
                                    className="restart-button"
                                    title="Restart Bot"
                                    disabled={!isCapturing || isRestarting}
                                >
                                    {isRestarting ? (
                                        <span className="button-spinner"></span>
                                    ) : (
                                        <span className="button-icon">🔄</span>
                                    )}
                                    <span className="button-text">
                                        {isRestarting ? 'Restarting...' : 'Restart'}
                                    </span>
                                </button>
                            </div>
                        )}
                    </div>
                    {error && (
                        <div className="error-message">
                            <div className="error-content">
                                <span className="error-icon">⚠️</span>
                                <div className="error-details">
                                    <h3>{error.message || 'Something went wrong'}</h3>
                                    {error.details && <p>{error.details}</p>}
                                </div>
                            </div>
                            <button
                                className="dismiss-button"
                                onClick={() => setError(null)}
                                aria-label="Dismiss error message"
                            >
                                &times;
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="control-panel">
                <button
                    className={`control-button ${isCapturing ? 'stop' : 'start'}`}
                    onClick={isCapturing ? handleStopCapture : handleStartCapture}
                    disabled={status === 'starting' || status === 'stopping'}
                    aria-busy={status === 'starting' || status === 'stopping'}
                >
                    <span className="button-content">
                        {status === 'starting' && (
                            <span className="button-spinner"></span>
                        )}
                        {status === 'stopping' ? 'Stopping...' : isCapturing ? 'Stop bot' : 'Start bot'}
                    </span>
                </button>
                
                {status === 'active' && (
                    <div className="last-updated">
                        Last updated: {new Date().toLocaleTimeString()}
                    </div>
                )}
            </div>

            <div className="stats-panel">
                <h2>
                    Capture Statistics
                    <button 
                        className="refresh-button"
                        onClick={fetchStatus}
                        aria-label="Refresh statistics"
                        title="Refresh statistics"
                    >
                        <span className="refresh-icon">↻</span>
                    </button>
                </h2>
                
                <div className="stats-grid">

                    
                    <div className="stat-item">
                        <label className="stat-label" htmlFor="directions-count">Trend Tracker</label>
                        <input
                            id="directions-count"
                            type="number"
                            min="0"
                            max="1000"
                            value={stats.numberOfDirections}
                            onChange={(e) => {
                                const value = Math.min(Math.max(0, parseInt(e.target.value) || 0), 1000);
                                setStats(prev => ({
                                    ...prev,
                                    numberOfDirections: value
                                }));
                            }}
                            className="number-input"
                        />
                    </div>

                    
                    <div className="stat-item">
                        <span className="stat-label">Current Tracker</span>
                        <span className="stat-value">
                            {stats.numberOfDirections || 0}
                        </span>
                    </div>
                    
                    <div className="stat-item">
                        <span className="stat-label">Latest Amount</span>
                        <span className="stat-value">
                            ${typeof stats.latestAmount === 'number' ? stats.latestAmount.toFixed(2) : '0.00'}
                        </span>
                    </div>
                    
                    <div className={`stat-item ${tradeStatus.active ? 'active-trade' : ''}`}>
                        <span className="stat-label">Trade Status</span>
                        <div className="trade-status">
                            <span className={`trade-direction ${tradeStatus.direction?.toLowerCase() || ''}`}>
                                {tradeStatus.direction || '—'}
                            </span>

                            <span className={`trade-direction ${tradeStatus.direction?.toLowerCase() || ''}`}>{tradeStatus.time_placement}</span>
                            <span className={`trade-direction ${tradeStatus.direction?.toLowerCase() || ''}`}>{tradeStatus.message}</span>
                        </div>
                    </div>
                    
                    <div className="stat-item full-width">
                        <form onSubmit={handleAmountSubmit} className="amount-form">
                            <div className="form-group">
                                <label htmlFor="amount-input" className="form-label">Set New Amount ($)</label>
                                <div className="input-group">
                                    <input
                                        id="amount-input"
                                        type="number"
                                        min="0.01"
                                        step="0.01"
                                        value={amountInput}
                                        onChange={(e) => setAmountInput(e.target.value)}
                                        className="form-input"
                                        placeholder="Enter amount"
                                    />
                                    <button type="submit" className="amount-submit">Set</button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
                
                <div className="stat-item full-width">
                    <div className="directions-header">
                        <span className="stat-label">Market Trends</span>
                        <span className="directions-count">
                            {stats.directions.length} {stats.directions.length === 1 ? 'trend' : 'trends'}
                        </span>
                    </div>
                    
                    <div className="trend-indicator">
                        {stats.directions.length > 0 ? (
                            <div className="trend-arrows">
                                {stats.directions.map((item, index) => {
                                    // Format the time_placement to show only the time part if it's a full timestamp
                                    let timeDisplay = item.time_placement;
                                    if (typeof item.time_placement === 'string' && item.time_placement.includes('T')) {
                                        try {
                                            const date = new Date(item.time_placement);
                                            timeDisplay = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                                        } catch (e) {
                                            console.error('Error formatting time:', e);
                                        }
                                    }
                                    
                                    return (
                                        <div 
                                            key={index} 
                                            className={`trend-arrow ${item.direction.toLowerCase()}`}
                                            title={`${item.direction} trend at ${timeDisplay}`}
                                        >
                                            {item.direction === 'UP' ? '↑' : '↓'}
                                            <span className="time-placement">{timeDisplay}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="empty-state">
                                <span className="empty-icon">📊</span>
                                <p>No trend data available</p>
                                <small>Start capturing to see market trends</small>
                            </div>
                        )}
                    </div>
                    
                    {stats.directions.length > 0 && (
                        <div className="trend-summary">
                            <div className="trend-summary-item">
                                <span className="trend-label">Last Signal:</span>
                                <span className={`trend-value ${stats.directions[0].direction.toLowerCase()}`}>
                                    {stats.directions[0].direction}
                                </span>

                            </div>
                            <div className="trend-summary-item">
                                <span className="trend-label">Time:</span>
                                <span className={`trend-value ${stats.directions[0].direction.toLowerCase()}`}>
                                    {stats.directions[0].time_placement}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            


            {/* Direction History Section */}
            <div className="history-panel">
                <div className="panel-header">
                    <h2>Tracker history</h2>
                    <div className="panel-actions">
                        <button 
                            onClick={() => fetchDirectionHistory(historyPage, historyLimit)}
                            className="refresh-button"
                            disabled={isLoadingHistory}
                            title="Refresh history"
                        >
                            {isLoadingHistory ? '⏳' : '↻'}
                        </button>
                        <button 
                            onClick={handleClearHistory}
                            className="clear-button"
                            disabled={isLoadingHistory || directionHistory.length === 0}
                            title="Clear history"
                        >
                            🗑️ Clear
                        </button>
                    </div>
                </div>
                
                {isLoadingHistory ? (
                    <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Loading history...</p>
                    </div>
                ) : directionHistory.length > 0 ? (
                    <div className="history-list">
                        <div className="history-header">
                            <span>Direction</span>
                            <span>Time</span>
                        </div>
                        {directionHistory.map((item, index) => (
                            <div key={index} className={`history-item ${item.direction.toLowerCase()}`}>
                                <span className="direction">
                                    {item.direction === 'UP' ? '↑' : '↓'}
                                    {item.direction}
                                </span>
                                <span className="time">
                                    {new Date(item.time_placement || item.created_at).toLocaleString()}
                                </span>
                            </div>
                        ))}
                        
                        {/* Pagination */}
                        <div className="pagination">
                            <button 
                                onClick={() => fetchDirectionHistory(historyPage - 1, historyLimit)}
                                disabled={historyPage <= 1 || isLoadingHistory}
                            >
                                Previous
                            </button>
                            <span>Page {historyPage}</span>
                            <button 
                                onClick={() => fetchDirectionHistory(historyPage + 1, historyLimit)}
                                disabled={(historyPage * historyLimit) >= totalHistory || isLoadingHistory}
                            >
                                Next
                            </button>
                        </div>
                        
                        <div className="pagination-info">
                            Showing {directionHistory.length} of {totalHistory} records
                        </div>
                    </div>
                ) : (
                    <div className="empty-state">
                        <span className="empty-icon">📋</span>
                        <p>No direction history found</p>
                        <small>Start capturing directions to see history</small>
                    </div>
                )}
            </div>


            <div className="status-updates">
                <div className="update-item">
                    <span className="update-label">Bot Status:</span>
                    <span className="update-value status-message">
                        {stats.statusMessage || 'Checking status...'}
                    </span>
                </div>
                <div className="update-item">
                    <span className="update-label">Polling:</span>
                    <span className="update-value">
                        {pollingRef.current ? 'Active' : 'Inactive'}
                    </span>
                </div>
                <div className="update-item">
                    <span className="update-label">Next Update:</span>
                    <span className="update-value">
                        {pollingRef.current ? '~5 seconds' : '--'}
                    </span>
                </div>
            </div>
        </div>
    );
};